import React, { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { UrlModalStrategyProps } from "./UrlModal";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { WaitlistFeature, useWaitlist } from "@/contexts/WaitlistContext";
import { AnalyticsFlowEvent } from "@/analytics/types";
import LoginComponent from "@/components/auth/LoginComponent";

function formatFeatureName(resource: string | null): string {
    if (!resource) return "this feature";

    return resource
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export const WaitlistModal: React.FC<UrlModalStrategyProps> = ({ isOpen, onClose, resource }) => {
    const { track } = useAnalytics();
    const { user, initialLoadComplete } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const { currentWorkspace } = useCurrentWorkspace();

    const { hasJoinedWaitlist, joinWaitlist, leaveWaitlist } = useWaitlist();

    const wasAlreadyJoined = useMemo(() => {
        return isOpen ? hasJoinedWaitlist(user?.uid ?? null, resource as WaitlistFeature) : false;
    }, [isOpen, resource, user?.uid, hasJoinedWaitlist]);

    const hasTrackedJoinRef = useRef(false);

    useEffect(() => {
        hasTrackedJoinRef.current = false;
    }, [isOpen, resource]);

    useEffect(() => {
        if (!isOpen || !resource || !user?.email || !user?.uid || hasTrackedJoinRef.current) return;

        const alreadyJoined = hasJoinedWaitlist(user.uid, resource as WaitlistFeature);
        if (alreadyJoined) {
            hasTrackedJoinRef.current = true;
            return;
        }

        joinWaitlist(user.uid, resource as WaitlistFeature);
        hasTrackedJoinRef.current = true;
        track({
            event: AnalyticsFlowEvent.WAITLIST_JOINED,
            data: { resource, email: user.email, userId: user.uid }
        });
    }, [isOpen, resource, user, track, hasJoinedWaitlist, joinWaitlist]);

    function handleClose() {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete("open");
        searchParams.delete("for");
        const cleanSearch = searchParams.toString();
        const searchString = cleanSearch ? `?${cleanSearch}` : "";

        if (currentWorkspace && !location.pathname.startsWith(`/${currentWorkspace.url}`)) {
            navigate(`/${currentWorkspace.url}${searchString}`, { replace: true });
        } else {
            onClose();
        }
    }

    function handleLeaveWaitlist() {
        if (!resource || !user?.uid) return;

        leaveWaitlist(user.uid, resource as WaitlistFeature);
        track({
            event: AnalyticsFlowEvent.WAITLIST_LEFT,
            data: { resource, email: user.email, userId: user.uid }
        });
        handleClose();
    }

    if (!initialLoadComplete) {
        return null;
    }

    if (!user) {
        return (
            <LoginComponent
                isOpen={isOpen}
                onClose={handleClose}
                title={
                    <div className="flex flex-col gap-0.5 text-center">
                        <span className="font-semibold">Sign In or Create an Account</span>
                        <span className="text-sm font-medium text-tertiary">
                            to join the {formatFeatureName(resource)} waitlist
                        </span>
                    </div>
                }
            />
        );
    }

    return (
        /* @ts-expect-error - ModalWithIcon component type issue
         */
        <ModalWithIcon
            isOpen={isOpen}
            onClose={handleClose}
            onSubmit={handleClose}
            type="success"
            size="md"
            icon="far fa-check"
            title={
                wasAlreadyJoined
                    ? "You've already joined the waitlist"
                    : "Congrats, you've joined the waitlist!"
            }
            submitButtonText="Close"
            submitButtonProps={{ type: "secondary" }}
            className="text-center"
        >
            <p>
                We'll email you at <span className="font-semibold">{user.email}</span> when{" "}
                <span className="whitespace-nowrap font-semibold">
                    {formatFeatureName(resource)}
                </span>{" "}
                is ready to use.
            </p>
            {wasAlreadyJoined && (
                <p className="font-sans text-xs text-gray-500">
                    Changed your mind?{" "}
                    <button
                        onClick={handleLeaveWaitlist}
                        className="cursor-pointer text-tertiary underline transition-colors duration-200 hover:text-primary"
                    >
                        Leave the waitlist
                    </button>
                </p>
            )}
        </ModalWithIcon>
    );
};
